import { createContext, useContext, useRef, useState, useEffect, useMemo, ReactNode } from 'react';
import { Socket, io } from 'socket.io-client';
import { useKeycloakAuth } from './KeycloakAuthProvider';

interface IAdminSocketContext {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;
}

const AdminSocketContext = createContext<IAdminSocketContext>({
  socket: null,
  isConnected: false,
  isConnecting: false,
  error: null
});

export const useAdminSocket = () => useContext(AdminSocketContext);

export const AdminSocketProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useKeycloakAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Keep latest token in ref so reconnection attempts use a fresh token
  const tokenRef = useRef(user?.accessToken);
  useEffect(() => { tokenRef.current = user?.accessToken; }, [user?.accessToken]);

  useEffect(() => {
    if (!user?.id || !user?.accessToken) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const socketAuth = socket?.auth as { userId?: string } | undefined;
    if (socket?.connected && socketAuth?.userId === user.id) return;

    setIsConnecting(true);
    setError(null);

    if (socket) socket.disconnect();

    const newSocket = io('/', {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      // Use callback so Socket.IO fetches the latest token on each reconnection attempt
      auth: (cb) => { cb({ token: tokenRef.current ?? '', userId: user.id }); }
    });

    newSocket.on('connect', () => {
      setIsConnecting(false);
      setIsConnected(true);
      console.log('[AdminSocket] Connected:', newSocket.id);
    });

    newSocket.on('connect_error', (err: Error) => {
      setError(err);
      setIsConnecting(false);
      setIsConnected(false);
    });

    newSocket.on('disconnect', (reason: string) => {
      setIsConnected(false);
      console.log('[AdminSocket] Disconnected:', reason);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect only when user changes
  }, [user?.id]);

  const contextValue = useMemo(
    () => ({ socket, isConnected, isConnecting, error }),
    [socket, isConnected, isConnecting, error]
  );

  return <AdminSocketContext.Provider value={contextValue}>{children}</AdminSocketContext.Provider>;
};
